/**
 * Infrastructure Cell Renderers
 *
 * Provides custom cell renderer components for infrastructure data tables.
 * Includes status display formatting and classifier log link functionality.
 */

import { useCallback } from 'react';
import { columnValueGetter } from '../../data-table/DataTableColumns';
import { ID_MAPPING, IPADDRESS_FIELD, UISTATE_FIELD } from './InfrastructureConstants';
import { DEVICE_TYPE_FIELD } from './device/DeviceConstants';

const STATUS_DOWN = 'Down';
const SYSLOG_EMERGENCY_COLOR = '#EB7861';

/**
 * Component to render "Down" status with emergency styling.
 */
const DownStatus = () => {
    return <span style={{ color: SYSLOG_EMERGENCY_COLOR }}>{STATUS_DOWN}</span>;
};

/**
 * Renders a status cell with special formatting for "Down" status.
 *
 * @param {Object} row - The row data object
 * @param {Object} column - The column configuration object
 * @returns {JSX.Element} Rendered status cell
 */
export const renderStatus = (row, column) => {
    const value = columnValueGetter(row, column);
    if (value === STATUS_DOWN) {
        return <DownStatus />;
    }

    return <span>{value}</span>;
};

/**
 * Link component that opens classifier log dialog when clicked.
 */
const ClassifierLogLink = ({ showClassifierLogDialog, row, column }) => {
    const handleClick = useCallback(
        (event) => {
            event.currentTarget.blur();
            event.preventDefault();
            event.stopPropagation();

            const classifierLogOption = {
                [IPADDRESS_FIELD]: row[IPADDRESS_FIELD],
                [ID_MAPPING]: row[ID_MAPPING],
                [DEVICE_TYPE_FIELD]: row[DEVICE_TYPE_FIELD],
                [UISTATE_FIELD]: row[UISTATE_FIELD],
            };
            showClassifierLogDialog(classifierLogOption);
        },
        [showClassifierLogDialog, row]
    );

    return (
        // eslint-disable-next-line jsx-a11y/anchor-is-valid
        <a href="#" onClick={handleClick}>
            {renderStatus(row, column)}
        </a>
    );
};

/**
 * Renderer function for classifier log links.
 * Wraps status rendering in a clickable link.
 *
 * @param {Function} showClassifierLogDialog - Callback to show classifier log dialog
 * @param {Object} row - The row data object
 * @param {Object} column - The column configuration object
 * @returns {JSX.Element} Rendered classifier log link
 */
export const classifierLogLinkRenderer = (showClassifierLogDialog, row, column) => {
    return <ClassifierLogLink showClassifierLogDialog={showClassifierLogDialog} row={row} column={column} />;
};
